/**
 * storefrontCategoryPath -- the ONE place a storefront category LINK target
 * is built.
 *
 * Jake, 2026-08-26: "If a slug is empty there should be no link. Those would
 * be broken links anyway."
 *
 * An empty category slug is NOT a data defect. It is the HOME category's
 * real, designed slug: api/src/models/store_category.py documents the
 * ``is_home`` row as "the ONE exception to 'always non-empty'" and its
 * constructor sets ``kwargs["slug"] = ""`` for it. Home lives at
 * ``/shop/<store>`` and owns no ``/c/`` segment, so ``/shop/<store>/c/``
 * matches NO route (``storefront_category`` is ``c/:categorySlug``, which
 * requires at least one character) and can never be a valid link target.
 *
 * Why a helper rather than a truthiness check at each call site: the bug this
 * fixes WAS a per-site check -- ProductPage guarded on the category NAME
 * while building the URL from the SLUG, so a category named "Home" with an
 * empty slug slipped through. N hand-rolled guards is a rule the N+1th call
 * site will miss in exactly the same way. Returning ``null`` (rather than a
 * string) forces the caller to make the no-link case explicit; a caller that
 * forgets and renders ``:to="null"`` fails loudly instead of silently
 * emitting a dead href (docs/design-philosophy.md: one mechanism per concept,
 * single-source state).
 *
 * NOTE this governs category LINKS only. It deliberately does NOT touch the
 * PRODUCT url shape ``/shop/<store>/c/<categorySlug>/p/<id>``, whose empty
 * ``/c//`` form for Home is load-bearing and resolves by design -- the
 * product route declares ``c/:categorySlug([^/]*)/p/:id`` precisely so it
 * matches the empty string, and there is no bare ``p/:id`` route to fall back
 * on. See the comment block on that route in router/groups/storefront.js.
 *
 * @param {string} storeSlug     the store's slug (the /shop/<store> segment)
 * @param {string} categorySlug  the category's slug ("" for Home)
 * @returns {string|null} the category path, or null when no link is possible
 */
export function storefrontCategoryPath(storeSlug, categorySlug) {
    if (typeof storeSlug !== 'string' || storeSlug.trim() === '') return null
    if (typeof categorySlug !== 'string' || categorySlug.trim() === '') {
        return null
    }
    return `/shop/${storeSlug.trim()}/c/${categorySlug.trim()}`
}

export default storefrontCategoryPath
